import { useState } from 'react';
import { Download, Loader, FileText } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';

export default function ExportReport() {
  const [loading, setLoading] = useState(false);

  const generatePDF = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/export/mood-report`);
      const d = res.data;

      // Build HTML for PDF
      const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Mind My Mojo — Emotional Wellness Report</title>
  <style>
    body { font-family: Georgia, serif; max-width: 800px; margin: 0 auto; padding: 40px 30px; color: #1C1917; }
    h1 { color: #3A5A40; font-size: 28px; border-bottom: 2px solid #3A5A40; padding-bottom: 10px; }
    h2 { color: #3A5A40; font-size: 20px; margin-top: 30px; }
    .meta { color: #78716C; font-size: 13px; margin-bottom: 20px; }
    .stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 15px 0; }
    .stat-box { background: #F5F5F4; padding: 15px; border-radius: 10px; }
    .stat-box .label { font-size: 12px; color: #78716C; }
    .stat-box .value { font-size: 24px; font-weight: bold; color: #3A5A40; }
    .chart-container { margin: 20px 0; }
    .chart-bar { display: flex; align-items: center; margin: 4px 0; }
    .chart-label { width: 120px; font-size: 11px; text-align: right; padding-right: 10px; }
    .chart-bar-bg { flex: 1; height: 20px; background: #E7E5E4; border-radius: 10px; overflow: hidden; position: relative; }
    .chart-bar-fill { height: 100%; border-radius: 10px; }
    .chart-bar-pct { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); font-size: 10px; font-weight: bold; color: #44403C; }
    .bar-container { margin: 8px 0; }
    .bar-label { font-size: 13px; margin-bottom: 3px; }
    .bar-bg { height: 18px; background: #E7E5E4; border-radius: 9px; overflow: hidden; }
    .bar-fill { height: 100%; background: #3A5A40; border-radius: 9px; }
    table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; }
    th { text-align: left; padding: 8px; background: #3A5A40; color: white; }
    td { padding: 8px; border-bottom: 1px solid #E7E5E4; }
    tr:nth-child(even) { background: #FAFAF9; }
    .footer { margin-top: 40px; padding-top: 15px; border-top: 1px solid #E7E5E4; font-size: 11px; color: #A8A29E; text-align: center; }
  </style>
</head>
<body>
  <h1>Emotional Wellness Report</h1>
  <p class="meta">${d.user_name} — ${d.date_range.from || 'N/A'} to ${d.date_range.to || 'N/A'}</p>

  <div class="stat-grid">
    <div class="stat-box"><div class="label">Total Check-ins</div><div class="value">${d.total_entries}</div></div>
    <div class="stat-box"><div class="label">Date Range</div><div class="value" style="font-size:16px">${d.date_range.from || '—'} to ${d.date_range.to || '—'}</div></div>
  </div>

  <h2>Top Emotion Categories</h2>
  <div class="chart-container">
  ${d.top_categories.map(c => {
    const pct = Math.min(100, (c.count / (d.total_entries || 1)) * 100);
    return `
    <div class="chart-bar">
      <div class="chart-label">${c.name}</div>
      <div class="chart-bar-bg">
        <div class="chart-bar-fill" style="width:${pct}%; background:#3A5A40;"></div>
        <div class="chart-bar-pct">${c.count} (${pct.toFixed(0)}%)</div>
      </div>
    </div>`;
  }).join('')}
  </div>

  ${Object.keys(d.daily_entries).length > 0 ? `
  <h2>Daily Check-in Activity</h2>
  <div style="margin:15px 0; overflow-x:auto;">
    <svg width="700" height="200" viewBox="0 0 700 200">
      <line x1="40" y1="170" x2="680" y2="170" stroke="#E7E5E4" stroke-width="1"/>
      ${Object.entries(d.daily_entries).slice(-30).map(([day, count], i, arr) => {
        const maxCount = Math.max(...arr.map(a => a[1]));
        const x = 50 + (i / Math.max(arr.length - 1, 1)) * 620;
        const h = (count / Math.max(maxCount, 1)) * 140;
        const y = 170 - h;
        return `<rect x="${x - 8}" y="${y}" width="16" height="${h}" rx="4" fill="#3A5A40" opacity="0.7"/>
                <text x="${x}" y="185" text-anchor="middle" font-size="8" fill="#78716C">${day.slice(5)}</text>`;
      }).join('')}
    </svg>
  </div>
  ` : ''}

  <h2>Most Frequent Feelings</h2>
  ${d.top_feelings.map(f => `
    <div class="bar-container">
      <div class="bar-label">${f.name} — ${f.count}x</div>
      <div class="bar-bg"><div class="bar-fill" style="width:${Math.min(100, (f.count / (d.total_entries || 1)) * 100 * 5)}%"></div></div>
    </div>
  `).join('')}

  <h2>Recent Entries</h2>
  <table>
    <tr><th>Date</th><th>Category</th><th>Feeling</th><th>Intensity</th><th>Notes</th></tr>
    ${d.entries.slice(0, 30).map(e => `
      <tr>
        <td>${e.date}</td>
        <td>${e.category}</td>
        <td>${e.feeling}</td>
        <td>${e.intensity}/10</td>
        <td>${e.reason ? e.reason.substring(0, 50) : '—'}</td>
      </tr>
    `).join('')}
  </table>

  <div class="footer">
    Generated by Mind My Mojo — mindmymojo.com<br>
    This report is intended to support emotional wellness conversations with healthcare professionals.
  </div>
</body>
</html>`;

      // Open print dialog which allows Save as PDF
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };

      toast.success('Report generated! Use "Save as PDF" in the print dialog.');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to generate report. Record some emotions first!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={generatePDF}
      disabled={loading}
      variant="outline"
      className="flex items-center gap-2 text-sm"
      data-testid="export-report-btn"
    >
      {loading ? <Loader className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
      {loading ? 'Generating...' : 'Export PDF Report'}
    </Button>
  );
}
